
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Service, ServiceContractType, ContractService } from "@/lib/types/services";

// Create a request deduplication cache
const activeFetchRequests = new Map<string, Promise<any>>();

export function useServices() {
  const queryClient = useQueryClient();

  // Helper function to deduplicate identical fetch requests
  const dedupedFetch = async <T>(
    key: string, 
    fetchFn: () => Promise<T>,
    ttl = 5000 // Time to keep in the active requests map (ms)
  ): Promise<T> => {
    if (!activeFetchRequests.has(key)) {
      const promise = fetchFn()
        .finally(() => {
          setTimeout(() => {
            activeFetchRequests.delete(key);
          }, ttl);
        });
      
      activeFetchRequests.set(key, promise);
    }
    
    return activeFetchRequests.get(key) as Promise<T>;
  };

  const useServicesQuery = (options = {}) => {
    return useQuery({
      queryKey: ["services"],
      queryFn: async () => {
        const fetchKey = "services-all";
        return dedupedFetch(fetchKey, async () => {
          const { data, error } = await supabase
            .from("services")
            .select(`*, currency:currencies(code, symbol)`)
            .order("name");

          if (error) {
            console.error("Error fetching services:", error);
            throw error;
          }

          return data as Service[];
        });
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes - changed from cacheTime to gcTime
      ...options
    });
  };

  const useServiceQuery = (id: string, options = {}) => {
    return useQuery({
      queryKey: ["services", id],
      queryFn: async () => {
        if (!id) return null;
        
        const fetchKey = `service-${id}`;
        return dedupedFetch(fetchKey, async () => {
          const { data, error } = await supabase
            .from("services")
            .select(`*, currency:currencies(code, symbol)`)
            .eq("id", id)
            .single();

          if (error) {
            console.error("Error fetching service:", error);
            throw error;
          }

          return data as Service;
        });
      },
      enabled: !!id,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000, // Changed from cacheTime to gcTime
      ...options
    });
  };

  const useServicesForContractTypeQuery = (contractTypeId: string, options = {}) => {
    return useQuery({
      queryKey: ["services", "for-contract-type", contractTypeId],
      queryFn: async () => {
        if (!contractTypeId) return [];
        
        const fetchKey = `services-for-contract-type-${contractTypeId}`;
        return dedupedFetch(fetchKey, async () => {
          const { data, error } = await supabase
            .from("service_contract_types")
            .select(`service_id`)
            .eq("contract_type_id", contractTypeId);

          if (error) {
            console.error("Error fetching service contract types:", error);
            throw error;
          }

          if (data.length === 0) {
            return [];
          }

          const serviceIds = data.map(item => item.service_id);
          
          const { data: services, error: servicesError } = await supabase
            .from("services")
            .select(`*, currency:currencies(code, symbol)`)
            .in("id", serviceIds);

          if (servicesError) {
            console.error("Error fetching services:", servicesError);
            throw servicesError;
          }

          return services as Service[];
        });
      },
      enabled: !!contractTypeId,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000, // Changed from cacheTime to gcTime
      retry: 2,
      ...options
    });
  };

  const useContractServicesQuery = (contractId: string, options = {}) => {
    return useQuery({
      queryKey: ["contract-services", contractId],
      queryFn: async () => {
        if (!contractId) return [];
        
        const fetchKey = `contract-services-${contractId}`;
        return dedupedFetch(fetchKey, async () => {
          const { data, error } = await supabase
            .from("contract_services")
            .select(`*, service:services(*, currency:currencies(code, symbol))`)
            .eq("contract_id", contractId);

          if (error) {
            console.error("Error fetching contract services:", error);
            throw error;
          }

          return data as ContractService[];
        });
      },
      enabled: !!contractId,
      staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes to reduce API calls
      gcTime: 10 * 60 * 1000, // Changed from cacheTime to gcTime
      retry: 2, // Retry failed requests twice
      refetchOnWindowFocus: false, // Don't refetch when window regains focus
      refetchOnMount: true, // Refetch when component mounts
      ...options
    });
  };

  const useCreateServiceMutation = () => {
    return useMutation({
      mutationFn: async (service: Omit<Service, "id" | "user_id" | "created_at" | "updated_at">) => {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          throw new Error("User not authenticated");
        }
        
        const serviceWithUserId = {
          ...service,
          user_id: user.id
        };
        
        const { data, error } = await supabase
          .from("services")
          .insert(serviceWithUserId)
          .select()
          .single();

        if (error) {
          console.error("Error creating service:", error);
          throw error;
        }

        return data as Service;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["services"] });
      },
      onError: (error) => {
        console.error("Failed to create service:", error);
        toast.error("Failed to create service");
      },
    });
  };

  const useUpdateServiceMutation = () => {
    return useMutation({
      mutationFn: async (service: Partial<Service> & { id: string }) => {
        const { data, error } = await supabase
          .from("services")
          .update(service)
          .eq("id", service.id)
          .select()
          .single();

        if (error) {
          console.error("Error updating service:", error);
          throw error;
        }

        return data as Service;
      },
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({ queryKey: ["services"] });
        queryClient.invalidateQueries({ queryKey: ["services", variables.id] });
      },
      onError: (error) => {
        console.error("Failed to update service:", error);
        toast.error("Failed to update service");
      },
    });
  };

  const useDeleteServiceMutation = () => {
    return useMutation({
      mutationFn: async (id: string) => {
        const { error } = await supabase
          .from("services")
          .delete()
          .eq("id", id);

        if (error) {
          console.error("Error deleting service:", error);
          throw error;
        }

        return id;
      },
      onSuccess: (id) => {
        queryClient.invalidateQueries({ queryKey: ["services"] });
        queryClient.invalidateQueries({ queryKey: ["services", id] });
      },
      onError: (error) => {
        console.error("Failed to delete service:", error);
        toast.error("Failed to delete service");
      },
    });
  };

  const useLinkServiceToContractTypesMutation = () => {
    return useMutation({
      mutationFn: async ({ serviceId, contractTypeIds }: { serviceId: string, contractTypeIds: string[] }) => {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          throw new Error("User not authenticated");
        }
        
        const { error: deleteError } = await supabase
          .from("service_contract_types")
          .delete()
          .eq("service_id", serviceId);

        if (deleteError) {
          console.error("Error deleting service contract types:", deleteError);
          throw deleteError;
        }

        if (contractTypeIds.length === 0) {
          return { serviceId, contractTypeIds: [] };
        }

        const links = contractTypeIds.map(contractTypeId => ({
          service_id: serviceId,
          contract_type_id: contractTypeId,
          user_id: user.id
        }));

        const { error: insertError } = await supabase
          .from("service_contract_types")
          .insert(links);

        if (insertError) {
          console.error("Error linking service to contract types:", insertError);
          throw insertError;
        }

        return { serviceId, contractTypeIds };
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["services"] });
      },
      onError: (error) => {
        console.error("Failed to link service to contract types:", error);
        toast.error("Failed to link service to contract types");
      },
    });
  };

  const useAddServiceToContractMutation = () => {
    return useMutation({
      mutationFn: async ({ contractId, serviceId, quantity = 1 }: { contractId: string, serviceId: string, quantity?: number }) => {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          throw new Error("User not authenticated");
        }
        
        const { data: existing, error: checkError } = await supabase
          .from("contract_services")
          .select("id")
          .eq("contract_id", contractId)
          .eq("service_id", serviceId)
          .maybeSingle();

        if (checkError) {
          console.error("Error checking existing contract service:", checkError);
          throw checkError;
        }

        if (existing) {
          const { data, error } = await supabase
            .from("contract_services")
            .update({ quantity })
            .eq("id", existing.id)
            .select()
            .single();

          if (error) {
            console.error("Error updating contract service:", error);
            throw error;
          }

          return data as ContractService;
        } else {
          const { data, error } = await supabase
            .from("contract_services")
            .insert({
              contract_id: contractId,
              service_id: serviceId,
              quantity,
              user_id: user.id
            })
            .select()
            .single();

          if (error) {
            console.error("Error adding service to contract:", error);
            throw error;
          }

          return data as ContractService;
        }
      },
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({ queryKey: ["contract-services", variables.contractId] });
        queryClient.invalidateQueries({ queryKey: ["contracts", variables.contractId] });
      },
      onError: (error) => {
        console.error("Failed to add service to contract:", error);
        toast.error("Failed to add service to contract");
      },
    });
  };

  const useRemoveServiceFromContractMutation = () => {
    return useMutation({
      mutationFn: async (contractServiceId: string) => {
        const { data: contractService, error: fetchError } = await supabase
          .from("contract_services")
          .select("contract_id")
          .eq("id", contractServiceId)
          .maybeSingle();

        if (fetchError) {
          console.error("Error fetching contract service:", fetchError);
          throw fetchError;
        }

        if (!contractService) {
          throw new Error(`Contract service with ID ${contractServiceId} not found`);
        }

        const contractId = contractService.contract_id;

        const { error } = await supabase
          .from("contract_services")
          .delete()
          .eq("id", contractServiceId);

        if (error) {
          console.error("Error removing service from contract:", error);
          throw error;
        }

        return { contractServiceId, contractId };
      },
      onSuccess: (result) => {
        queryClient.invalidateQueries({ queryKey: ["contract-services", result.contractId] });
        queryClient.invalidateQueries({ queryKey: ["contracts", result.contractId] });
      },
      onError: (error) => {
        console.error("Failed to remove service from contract:", error);
        toast.error("Failed to remove service from contract");
      },
    });
  };

  const useUpdateContractServiceMutation = () => {
    return useMutation({
      mutationFn: async ({ id, quantity }: { id: string, quantity: number }) => {
        const { data: contractService, error: fetchError } = await supabase
          .from("contract_services")
          .select("contract_id")
          .eq("id", id)
          .maybeSingle();

        if (fetchError) {
          console.error("Error fetching contract service:", fetchError);
          throw fetchError;
        }

        if (!contractService) {
          throw new Error(`Contract service with ID ${id} not found`);
        }

        const contractId = contractService.contract_id;

        const { data, error } = await supabase
          .from("contract_services")
          .update({ quantity })
          .eq("id", id)
          .select()
          .single();

        if (error) {
          console.error("Error updating contract service:", error);
          throw error;
        }

        return { ...data, contractId } as ContractService & { contractId: string };
      },
      onSuccess: (result) => {
        queryClient.invalidateQueries({ queryKey: ["contract-services", result.contractId] });
        queryClient.invalidateQueries({ queryKey: ["contracts", result.contractId] });
      },
      onError: (error) => {
        console.error("Failed to update contract service:", error);
        toast.error("Failed to update contract service");
      },
    });
  };

  return {
    useServicesQuery,
    useServiceQuery,
    useServicesForContractTypeQuery,
    useContractServicesQuery,
    useCreateServiceMutation,
    useUpdateServiceMutation,
    useDeleteServiceMutation,
    useLinkServiceToContractTypesMutation,
    useAddServiceToContractMutation,
    useRemoveServiceFromContractMutation,
    useUpdateContractServiceMutation
  };
}
